// Source Catalog — every data pipeline this dashboard knows about.
// Status is what an engineer needs to ship the layer:
//   live      — adapter wired, endpoint working, no key required (or key shipped via env)
//   ready     — adapter scaffolded; flip a switch / drop a key to go live
//   planned   — known endpoint, no adapter yet; one afternoon of work
//   research  — we know the data exists but not the public endpoint
//
// Categories drive the "Source Catalog" panel groupings.

export type SourceStatus = "live" | "ready" | "planned" | "research";

export type SourceCategory =
  | "mobility"      // traffic, transit, shuttle
  | "incidents"     // citizen reports, traffic events
  | "environment"   // weather, air, water, vegetation
  | "imagery"       // satellite, aerial, CCTV
  | "vibes"         // news, social, search trends
  | "infrastructure" // utilities, parking, energy
  | "campus";       // kmitl-specific (campus GIS, dorms, research, shuttle)

export interface SourceEntry {
  id: string;
  label: string;
  vendor: string;
  category: SourceCategory;
  status: SourceStatus;
  endpoint?: string;     // URL or pattern
  apiPath?: string;      // path served by our API (when live)
  keyEnv?: string;       // env var name if a key is required
  pollSeconds?: number;
  describe: string;      // 1-line, engineer-readable
  docs?: string;
}

export const SOURCE_CATALOG: SourceEntry[] = [
  // ---- Mobility ----
  {
    id: "itic-longdo",
    label: "iTIC / Longdo traffic events",
    vendor: "iTIC + Longdo Map",
    category: "mobility",
    status: "live",
    endpoint: "https://event.longdo.com/feed/json",
    apiPath: "/api/incidents/itic",
    pollSeconds: 180,
    describe: "Accidents, closures, construction, congestion events with lat/lng + severity.",
    docs: "https://map.longdo.com/products/traffic",
  },
  {
    id: "longdo-cameras",
    label: "Longdo CCTV cameras",
    vendor: "Longdo Map",
    category: "imagery",
    status: "ready",
    endpoint: "https://camera.longdo.com/feed/?command=json",
    apiPath: "/api/cctv/longdo",
    pollSeconds: 300,
    describe: "Hundreds of street CCTV with JPG/HLS streams; filter to the Lat Krabang / Chalongkrung Road bbox around campus.",
    docs: "https://camera.longdo.com",
  },
  {
    id: "bma-cctv",
    label: "BMA Traffic CCTV",
    vendor: "Bangkok Metropolitan Authority",
    category: "imagery",
    status: "research",
    endpoint: "https://www.bmatraffic.com/CCTV.aspx",
    describe: "BMA-operated traffic cameras; scrape catalog page or use ArcGIS feature service when located.",
    docs: "https://www.bmatraffic.com",
  },
  {
    id: "exat-cctv",
    label: "EXAT expressway cameras",
    vendor: "Expressway Authority of Thailand",
    category: "imagery",
    status: "research",
    endpoint: "https://www.exat.co.th/cctv/",
    describe: "Expressway cams; Bangkok–Chonburi Motorway (Route 7) segment runs ~2 km south of campus toward Suvarnabhumi.",
  },
  {
    id: "highways-th",
    label: "Department of Highways cameras",
    vendor: "Thai Department of Highways",
    category: "imagery",
    status: "research",
    endpoint: "https://highway.dxc-thailand.com/",
    describe: "DOH highway cameras; useful only if we extend the bbox out to the Bangkok–Chonburi Motorway entrances.",
  },
  {
    id: "namtang-gtfs",
    label: "Namtang GTFS (ARL + SRT + BMTA static)",
    vendor: "OTP (Office of Transport & Traffic Policy and Planning)",
    category: "mobility",
    status: "planned",
    endpoint: "https://namtang-api.otp.go.th",
    apiPath: "/api/transit/gtfs",
    pollSeconds: 86400,
    describe: "Thailand's primary open transit dataset (routes, stops, trips, shapes, fares). Carries ARL, SRT Eastern Line, and BMTA 517/143/1013 geometry. Daily refresh, free with attribution; no GTFS-Realtime.",
    docs: "https://namtang-api.otp.go.th",
  },
  {
    id: "arl-city-line",
    label: "Airport Rail Link (ARL) City Line",
    vendor: "SRT Electrified Train (SRTET)",
    category: "mobility",
    status: "research",
    endpoint: "https://www.srtet.co.th/index.php/en",
    describe: "28.6 km Phaya Thai (A8)–Suvarnabhumi (A1); Lat Krabang (A2) is ~4 km from campus, one stop / 15 THB / ~5 min to the airport. HTML timetables only — no API; use Namtang GTFS for schedule, scrape srtet.co.th for updates.",
    docs: "https://www.srtet.co.th/",
  },
  {
    id: "srt-eastern-line",
    label: "SRT Eastern Line — Pra Chomklao halt",
    vendor: "State Railway of Thailand",
    category: "mobility",
    status: "research",
    endpoint: "https://ttsview.railway.co.th/v3/",
    describe: "Pra Chomklao railway halt (code 3107) sits INSIDE the KMITL campus at km 30.33; ordinary/commuter trains to Hua Lamphong. SRT Train Tracking System returns HTML, no documented REST API — scheduled scraping of TTS results recommended.",
    docs: "https://ttsview.railway.co.th/",
  },
  {
    id: "bmta",
    label: "BMTA buses serving campus (517/143/1013/157)",
    vendor: "Bangkok Mass Transit Authority",
    category: "mobility",
    status: "planned",
    endpoint: "https://namtang-api.otp.go.th",
    describe: "Routes 517 (Hua Takhe–Chatuchak), 143 (Happyland–KMITL), 1013 (Onnut–Hua Takhe), 157 (Morchit 2–KMITL); stops ~100 m from Pra Chomklao. Route geometry via Namtang GTFS; live positions only in ViaBus/BMTA apps (no open API).",
  },
  {
    id: "google-traffic",
    label: "Google Maps traffic raster",
    vendor: "Google",
    category: "mobility",
    status: "planned",
    keyEnv: "GOOGLE_MAPS_KEY",
    describe: "Tile overlay; commercial license required. Use as a fallback comparison to iTIC + simulated heatmap.",
  },
  {
    id: "tomtom-traffic",
    label: "TomTom traffic flow",
    vendor: "TomTom",
    category: "mobility",
    status: "planned",
    keyEnv: "TOMTOM_KEY",
    describe: "Free 2,500 req/day tier; per-segment speed vs. free-flow — good replacement for simulated heatmap.",
    docs: "https://developer.tomtom.com/traffic-api",
  },
  {
    id: "here-traffic",
    label: "HERE traffic flow + incidents",
    vendor: "HERE Technologies",
    category: "mobility",
    status: "planned",
    keyEnv: "HERE_API_KEY",
    describe: "Per-link jam factor JSON; free tier 250k req/month.",
    docs: "https://developer.here.com/",
  },

  // ---- Incidents ----
  {
    id: "traffy-fondue",
    label: "Traffy Fondue citizen reports",
    vendor: "NECTEC / BMA",
    category: "incidents",
    status: "live",
    endpoint: "https://publicapi.traffy.in.th/dump-csv-stat-bma/bangkok_traffy.json",
    apiPath: "/api/incidents/city-reports",
    pollSeconds: 300,
    describe: "Citizen complaint feed (potholes, flooding, waste, lighting); bbox-filtered to outer campus area.",
    docs: "https://www.traffy.in.th/",
  },
  {
    id: "city-reporter-v2",
    label: "City Reporter v2 (own intake)",
    vendor: "Dr Non — internal",
    category: "incidents",
    status: "planned",
    apiPath: "/api/incidents/internal",
    describe: "LINE/Telegram intake writing to Supabase; same shape as Traffy. Drop in when client wants their own channel.",
  },
  {
    id: "1555-bma",
    label: "BMA 1555 complaint line",
    vendor: "Bangkok Metropolitan Authority",
    category: "incidents",
    status: "research",
    describe: "Phone hotline; intake stats published as monthly PDF. No realtime API.",
  },

  // ---- Environment ----
  {
    id: "open-meteo",
    label: "Weather fallback chain",
    vendor: "Open-Meteo + MET Norway",
    category: "environment",
    status: "live",
    endpoint: "https://api.open-meteo.com/v1/forecast + https://api.met.no/weatherapi/locationforecast/2.0/compact",
    apiPath: "/api/weather",
    pollSeconds: 1800,
    describe: "No-key weather. Open-Meteo first; MET Norway fills current weather when Open-Meteo rate-limits.",
  },
  {
    id: "world-weather",
    label: "World city weather strip",
    vendor: "Open-Meteo + MET Norway",
    category: "environment",
    status: "live",
    endpoint: "https://api.open-meteo.com/v1/forecast + https://api.met.no/weatherapi/locationforecast/2.0/compact",
    apiPath: "/api/world-weather",
    pollSeconds: 1800,
    describe: "Server-cached weather for Bangkok, Singapore, Tokyo, Shanghai, Berlin, Boston, and San Francisco. Keeps the browser from fan-out fetching.",
  },
  {
    id: "open-meteo-geocoding",
    label: "City clock geocoding",
    vendor: "Open-Meteo",
    category: "environment",
    status: "live",
    endpoint: "https://geocoding-api.open-meteo.com/v1/search",
    apiPath: "/api/geocode/cities",
    pollSeconds: 86400,
    describe: "Server-cached city search for custom clock slots.",
  },
  {
    id: "open-meteo-aq",
    label: "Open-Meteo air quality grid",
    vendor: "Open-Meteo",
    category: "environment",
    status: "live",
    endpoint: "https://air-quality-api.open-meteo.com/v1/air-quality",
    apiPath: "/api/air-quality",
    pollSeconds: 900,
    describe: "US AQI + PM2.5/PM10 from Copernicus reanalysis grid. Free, no key.",
  },
  {
    id: "air4thai-legacy-json",
    label: "Air4Thai stations",
    vendor: "Pollution Control Department",
    category: "environment",
    status: "ready",
    endpoint: "http://air4thai.com/forweb/getAQI_JSON.php",
    apiPath: "/api/air-quality/stations",
    pollSeconds: 900,
    describe: "Official Thai AQI station data. No PCD station in Lat Krabang itself — nearest are Bang Na and Wang Thonglang, so use as a regional baseline only.",
    docs: "http://air4thai.com/",
  },
  {
    id: "waqi",
    label: "WAQI (World AQI) tiles",
    vendor: "WAQI / EBAS",
    category: "environment",
    status: "planned",
    keyEnv: "WAQI_TOKEN",
    endpoint: "https://api.waqi.info/v2/feed/here/?token={token}",
    describe: "Crowd-sourced AQI with tile overlay; token from waqi.info.",
  },
  {
    id: "openaq",
    label: "OpenAQ historical",
    vendor: "OpenAQ",
    category: "environment",
    status: "planned",
    endpoint: "https://api.openaq.org/v2",
    describe: "Historical air-quality time series; useful for hour-of-day baselines, not realtime.",
  },
  {
    id: "tmd-weather",
    label: "TMD Thai Meteorological Dept.",
    vendor: "TMD",
    category: "environment",
    status: "planned",
    endpoint: "https://data.tmd.go.th/api/Weather3Hours/v1/",
    keyEnv: "TMD_KEY",
    describe: "Free Thai-language forecast with 3-hour granularity. Auth required (register).",
  },
  {
    id: "hii-thaiwater",
    label: "HII ThaiWater (WL + rain)",
    vendor: "Hydro-Informatics Institute",
    category: "environment",
    status: "live",
    endpoint: "https://api-v3.thaiwater.net/api/v1/thaiwater30/public/waterlevel_load",
    apiPath: "/api/flood-watch",
    pollSeconds: 600,
    describe: "Campus-scoped HII water-level + 24h rain gauges with FloodDash watch score (0–100). Nearest WL ~7 km (Bang Sao Thong); rain gauges on Khlong Prawet / Lat Krabang.",
    docs: "https://www.thaiwater.net",
  },
  {
    id: "bma-flood",
    label: "BMA flood + drainage",
    vendor: "BMA Drainage Dept.",
    category: "environment",
    status: "planned",
    endpoint: "https://weather.bangkok.go.th/Radar/",
    describe: "BMA flood gates, pump stations, weather radar. Used by Smart City Monitor; port adapter.",
  },
  {
    id: "gistda-flood",
    label: "GISTDA flood extent",
    vendor: "GISTDA",
    category: "environment",
    status: "ready",
    endpoint: "https://flood.gistda.or.th/",
    apiPath: "/api/gistda/flood",
    describe: "Sentinel-1 SAR-derived flood polygons during Thai monsoon. Returns empty/scenario until VIIRS-tier key.",
  },

  // ---- Imagery ----
  {
    id: "gibs-true-color",
    label: "NASA GIBS true-color (MODIS)",
    vendor: "NASA EOSDIS",
    category: "imagery",
    status: "live",
    endpoint: "https://gibs.earthdata.nasa.gov/wmts/epsg3857/best/MODIS_Terra_CorrectedReflectance_TrueColor/...",
    apiPath: "(client tile fetch)",
    describe: "1km true-color daily imagery; WMTS tile layer. Free, no key.",
    docs: "https://nasa-gibs.github.io/gibs-api-docs/",
  },
  {
    id: "gibs-ndvi",
    label: "NASA GIBS NDVI",
    vendor: "NASA EOSDIS",
    category: "imagery",
    status: "live",
    endpoint: "https://gibs.earthdata.nasa.gov/wmts/.../MODIS_Terra_NDVI_8Day/...",
    apiPath: "(client tile fetch)",
    describe: "Vegetation index 8-day composite. Useful for tracking campus greenery and heat-island gradient.",
  },
  {
    id: "sentinel-hub",
    label: "Sentinel Hub Process API",
    vendor: "ESA / Sinergise",
    category: "imagery",
    status: "planned",
    keyEnv: "SENTINEL_HUB_CLIENT_ID,SENTINEL_HUB_CLIENT_SECRET",
    endpoint: "https://services.sentinel-hub.com/api/v1/process",
    describe: "10m Sentinel-2 + Sentinel-1 SAR on demand. Free OAuth tier (30k PU/month).",
    docs: "https://docs.sentinel-hub.com/api/latest/",
  },
  {
    id: "gistda-theos",
    label: "GISTDA THEOS-2 / Pleiades",
    vendor: "GISTDA",
    category: "imagery",
    status: "planned",
    describe: "Thai high-res satellite; product order via dict.gistda.or.th, MOU needed for direct API.",
  },
  {
    id: "planetary-computer",
    label: "Microsoft Planetary Computer STAC",
    vendor: "Microsoft",
    category: "imagery",
    status: "planned",
    endpoint: "https://planetarycomputer.microsoft.com/api/stac/v1",
    describe: "STAC catalog + signed tile tokens; great for historical Sentinel/Landsat lookups.",
  },
  {
    id: "jaxa-himawari",
    label: "JAXA Himawari real-time",
    vendor: "JAXA / JMA",
    category: "imagery",
    status: "planned",
    endpoint: "https://www.eorc.jaxa.jp/ptree/index.html",
    describe: "Geostationary cloud loop, 10-min cadence. Use for storm approach.",
  },

  // ---- Vibes ----
  {
    id: "google-news-rss",
    label: "Google News RSS",
    vendor: "Google",
    category: "vibes",
    status: "live",
    endpoint: "https://news.google.com/rss/search?q=...",
    apiPath: "/api/news",
    pollSeconds: 300,
    describe: "Keyword-driven RSS (TH + EN + ZH) for KMITL / สจล. / Lat Krabang / ARL / Suvarnabhumi.",
  },
  {
    id: "bangkok-post-rss",
    label: "Bangkok Post top-stories RSS",
    vendor: "Bangkok Post",
    category: "vibes",
    status: "live",
    endpoint: "https://www.bangkokpost.com/rss/data/topstories.xml",
    apiPath: "/api/news",
    pollSeconds: 300,
    describe: "Higher-trust EN coverage; filtered to KMITL / Lat Krabang / Suvarnabhumi keywords inside our aggregator.",
  },
  {
    id: "thaipbs-rss",
    label: "Thai PBS English RSS",
    vendor: "Thai PBS",
    category: "vibes",
    status: "ready",
    endpoint: "https://www.thaipbsworld.com/feed/",
    describe: "Add to /api/news feed list.",
  },
  {
    id: "the-nation-rss",
    label: "The Nation RSS",
    vendor: "Nation Thailand",
    category: "vibes",
    status: "ready",
    endpoint: "https://www.nationthailand.com/rss",
    describe: "Add to /api/news feed list.",
  },
  {
    id: "talkwalker",
    label: "Talkwalker Free Alerts",
    vendor: "Talkwalker",
    category: "vibes",
    status: "planned",
    describe: "Email-driven alerts; route via webhook into news feed.",
  },
  {
    id: "reddit-rss",
    label: "Reddit r/Thailand + r/Bangkok",
    vendor: "Reddit",
    category: "vibes",
    status: "ready",
    endpoint: "https://www.reddit.com/r/Thailand+Bangkok/.rss",
    describe: "Public RSS; cap to keyword match around campus.",
  },
  {
    id: "google-trends",
    label: "Google Trends (campus terms)",
    vendor: "Google",
    category: "vibes",
    status: "planned",
    describe: "Daily/hourly trend deltas for 'KMITL', 'สจล.', 'ลาดกระบัง'; needs serpapi or unofficial pytrends.",
  },

  // ---- BMA portals — POI + datasets ----
  {
    id: "bma-gis",
    label: "BMA GIS POIs (ArcGIS FeatureServer)",
    vendor: "Bangkok Metropolitan Authority",
    category: "infrastructure",
    status: "live",
    endpoint: "https://bmagis.bangkok.go.th/arcgis/rest/services",
    apiPath: "/api/bma/pois",
    pollSeconds: 3600,
    describe: "Hospitals, schools, fire/police stations, parks, markets, BMA offices, flood gates, pump stations, CCTV, bus stops — multi-layer ArcGIS, bbox-filtered.",
    docs: "https://bmagis.bangkok.go.th",
  },
  {
    id: "bma-data-portal",
    label: "BMA Open Data Portal (CKAN)",
    vendor: "Bangkok Metropolitan Authority",
    category: "infrastructure",
    status: "live",
    endpoint: "https://data.bangkok.go.th/api/3/action/package_search",
    apiPath: "/api/bma/datasets",
    pollSeconds: 21600,
    describe: "Keyword search across BMA-published datasets (hospitals, schools, fire stations, PM2.5, parks, waste, drainage, accidents).",
    docs: "https://data.bangkok.go.th/",
  },
  {
    id: "bma-citydata-portal",
    label: "BMA City Data Portal (CKAN, alt)",
    vendor: "Bangkok Metropolitan Authority",
    category: "infrastructure",
    status: "ready",
    endpoint: "https://citydataportal.bangkok.go.th/api/3/action/package_search",
    describe: "Secondary CKAN catalog (older portal). Some datasets only exist here; fall back when data.bangkok.go.th returns 0.",
  },
  {
    id: "kmitl-campus-map",
    label: "KMITL interactive campus map (52 buildings)",
    vendor: "King Mongkut's Institute of Technology Ladkrabang",
    category: "campus",
    status: "research",
    endpoint: "https://kmitl-map.vercel.app",
    apiPath: "/geo/kmitl-buildings.geojson",
    describe: "Authoritative Thai/English building names for 52 structures across Zones A–D. No public API — coordinates hardcoded in the frontend bundle; extract via DevTools and pair with OSM footprints for georeferenced polygons.",
    docs: "https://kmitl-map.vercel.app",
  },
  {
    id: "osm-campus-footprints",
    label: "OSM campus footprints (Overpass)",
    vendor: "OpenStreetMap",
    category: "campus",
    status: "planned",
    endpoint: "https://overpass-api.de/api/interpreter",
    describe: "Community-mapped building polygons, roads, rail and POIs for KMITL; bbox ~13.71–13.74N / 100.77–100.79E captures campus structures plus Chalongkrung Road and the SRT Eastern Line. ODbL, free with attribution.",
    docs: "https://wiki.openstreetmap.org/wiki/Overpass_API",
  },
  {
    id: "kmitl-dormitory-belt",
    label: "Dormitory belt (aroundkmitl + RentHub)",
    vendor: "aroundkmitl.com / RentHub",
    category: "campus",
    status: "research",
    endpoint: "https://aroundkmitl.com",
    describe: "196+ private dorms within 5 km in five zones (RNP/Keki/Chalongkrung/FBT/Soi Jinda), 2,200–22,000 THB/month — an estimated 15,000–20,000 off-campus student beds. Geocode aroundkmitl listings, join RentHub prices, anchor on OSM building=dormitory polygons.",
    docs: "https://renthub.in.th/",
  },
  {
    id: "kmitl-kris-research",
    label: "KRIS research output (Scopus)",
    vendor: "KMITL Research & Innovation Services",
    category: "campus",
    status: "research",
    endpoint: "https://kris.kmitl.ac.th/en/",
    describe: "15,553 Scopus publications / 151,882 citations, engineering-dominant; grants, IP and KMITL Research Fund. No bulk export API — contact Research Administration Division; supplement with Scopus/EduRank bibliometrics and QS/Webometrics ranks.",
    docs: "https://kris.kmitl.ac.th/",
  },
  {
    id: "open-meteo-aq-trend",
    label: "Open-Meteo AQ 8h forecast (trend)",
    vendor: "Open-Meteo",
    category: "environment",
    status: "live",
    endpoint: "https://air-quality-api.open-meteo.com/v1/air-quality (hourly forecast)",
    apiPath: "/api/air-quality/trend",
    pollSeconds: 900,
    describe: "Drives the prominent AQI badge — current + next 8h US AQI + PM2.5 sparkline + advisory band.",
  },

  // ---- Infrastructure ----
  {
    id: "mea-outages",
    label: "MEA electricity outages",
    vendor: "Metropolitan Electricity Authority",
    category: "infrastructure",
    status: "planned",
    endpoint: "https://www.mea.or.th/profile/108",
    describe: "Scheduled outage notices; scrape into incident layer when timing intersects business hours.",
  },
  {
    id: "mwa-water",
    label: "MWA water outage notices",
    vendor: "Metropolitan Waterworks Authority",
    category: "infrastructure",
    status: "planned",
    endpoint: "https://www.mwa.co.th/article-detail/news-and-event/",
    describe: "Maintenance announcements affecting the Lat Krabang district.",
  },
  {
    id: "kmitl-sensor-network",
    label: "KMITL ESP32 sensor network (opportunity)",
    vendor: "KMITL — Engineering / IT faculties",
    category: "infrastructure",
    status: "research",
    describe: "No PCD AQ station and no permanent airport-noise monitor exist in Lat Krabang. ESP32-based PM2.5 + MEMS-microphone nodes (~$26–50/unit) deployed by student projects would close the gap and make the campus a data contributor to the city.",
  },
  {
    id: "kmitl-wifi-occupancy",
    label: "KDMC WiFi occupancy proxy",
    vendor: "KMITL Data Management Center (KDMC)",
    category: "infrastructure",
    status: "research",
    endpoint: "https://kdmc.kmitl.ac.th/en/services/wlan/",
    describe: "~3,200 access points across campus; association counts could proxy building-level occupancy, but KDMC exposes no public/authenticated API — needs an internal data-sharing agreement.",
  },

  // ---- Global markets — FMP + FRED ----
  {
    id: "fmp-markets",
    label: "FMP — global indices, forex, commodities",
    vendor: "Financial Modeling Prep",
    category: "vibes",
    status: "live",
    endpoint: "https://financialmodelingprep.com/api/v3/quote/...",
    apiPath: "/api/markets",
    keyEnv: "FMP_API_KEY",
    pollSeconds: 600,
    describe: "Live ticks for SET Bangkok + global indices (S&P, Nikkei, Hang Seng, FTSE, DAX), THB forex pairs, and commodities (WTI, Brent, Gold, Silver). Free tier 250/day.",
    docs: "https://site.financialmodelingprep.com/developer/docs",
  },
  {
    id: "fred-macro",
    label: "FRED — US macro + Thai baht series",
    vendor: "Federal Reserve Economic Data (St. Louis Fed)",
    category: "vibes",
    status: "live",
    endpoint: "https://api.stlouisfed.org/fred/series/observations?...",
    apiPath: "/api/markets",
    keyEnv: "FRED_API_KEY",
    pollSeconds: 600,
    describe: "Daily series: DGS10 (US 10Y yield), DGS2 (2Y), VIXCLS (volatility), DEXTHUS (THB/USD), DCOILWTICO (WTI). 120k calls/day.",
    docs: "https://fred.stlouisfed.org/docs/api/fred/",
  },

  // ---- Added 2026-05-17 from KMITL Data Source Bible research ----
  {
    id: "aqicn-lat-krabang",
    label: "AQICN / WAQI — Lat Krabang station (real-time)",
    vendor: "World Air Quality Index Project",
    category: "environment",
    status: "ready",
    endpoint: "https://api.waqi.info/feed/{station}/?token={AQICN_TOKEN}",
    keyEnv: "AQICN_TOKEN",
    pollSeconds: 900,
    describe: "Closest AQ to campus — WAQI station 'tplus531' in Lat Krabang subdistrict reports PM2.5/PM10/humidity/temp/wind. PCD has no station here. Register free at aqicn.org/data-platform/token.",
    docs: "https://aqicn.org/api/",
  },
  {
    id: "air4thai-pcd-network",
    label: "Air4Thai — PCD national AQ network",
    vendor: "Pollution Control Department (PCD)",
    category: "environment",
    status: "ready",
    endpoint: "http://air4thai.pcd.go.th/webV2/wsAPI/getAQI.php",
    pollSeconds: 900,
    describe: "Official Thai government AQ stations. No station in Lat Krabang — nearest are Bang Na and Wang Thonglang; use for regulatory-grade verification of the WAQI proxy. No key required; rate-limited.",
    docs: "http://air4thai.pcd.go.th/webV2/",
  },
  {
    id: "opensky-flights",
    label: "OpenSky Network — live ADS-B over campus",
    vendor: "OpenSky Network",
    category: "imagery",
    status: "ready",
    endpoint: "https://opensky-network.org/api/states/all?lamin=13.5&lamax=13.9&lomin=100.5&lomax=101.0",
    apiPath: "/api/aviation/flights",
    pollSeconds: 30,
    describe: "Live ADS-B positions in the KMITL–Suvarnabhumi (VTBS) corridor — callsign, position, altitude, velocity, heading. No key for basic use. Powers ops display and Aviation College student projects.",
    docs: "https://openskynetwork.github.io/opensky-api/",
  },
  {
    id: "checkwx-vtbs",
    label: "CheckWX — Suvarnabhumi (VTBS) aviation weather",
    vendor: "CheckWX",
    category: "environment",
    status: "ready",
    endpoint: "https://api.checkwx.com/metar/VTBS/decoded",
    keyEnv: "CHECKWX_API_KEY",
    pollSeconds: 1800,
    describe: "Decoded METAR/TAF for Suvarnabhumi (5–7 km east). Free tier 200 req/day; ICAO and geo lookups. Complements Open-Meteo with airfield-grade observations.",
    docs: "https://www.checkwxapi.com/",
  },
  {
    id: "bma-flood-platform",
    label: "BMA flood-risk platform (complaints + rainfall + drainage)",
    vendor: "Bangkok Metropolitan Administration",
    category: "environment",
    status: "research",
    endpoint: "https://www.bangkok.go.th/sub-portal/flood",
    describe: "Citizen flood complaints + rainfall gauges + drainage performance + canal levels fused in one BMA platform. Used by city ops; we need the underlying API/CKAN dataset names.",
    docs: "https://civicdatalab.in/work/climateaction/bma-flood-risk-data-platform/",
  },
  {
    id: "gistda-sphere",
    label: "GISTDA SPHERE — Thai national basemap",
    vendor: "GISTDA",
    category: "imagery",
    status: "research",
    endpoint: "https://sphere.gistda.or.th",
    describe: "2 m Thai basemap (vector + satellite + hybrid Thai labels) via WMS/WMTS/TMS, plus place search, reverse geocoding, routing and daily flood layers. Free API key. Candidate base tiles for a unified KMITL campus GIS.",
    docs: "https://sphere.gistda.or.th",
  },
  {
    id: "data-go-th",
    label: "data.go.th — Thai government open data",
    vendor: "DGA (Digital Government Agency)",
    category: "infrastructure",
    status: "research",
    endpoint: "https://data.go.th/",
    describe: "National open-data portal — population, urban planning, transport, education. CKAN-compatible API; aggregator layer needed.",
    docs: "https://data.go.th/dataset",
  },
];

export function sourcesByStatus(status: SourceStatus) {
  return SOURCE_CATALOG.filter((s) => s.status === status);
}

export function sourcesByCategory(category: SourceCategory) {
  return SOURCE_CATALOG.filter((s) => s.category === category);
}
